/**
 * Local Branch Management Hooks
 *
 * Simplified hooks that use the BranchContext for local state management
 * instead of server-side API calls. Provides CRUD functionality for branches.
 */

import { useBranchContext } from '@/context/BranchContext';
import type { BranchFormData } from '@/types/branch.types';

/**
 * Hook for getting all branches from local state
 */
export const useLocalBranches = () => {
  const { branches, isLoading, error } = useBranchContext();

  return {
    data: { data: branches },
    isLoading,
    error,
  };
};

/**
 * Hook for getting a single branch by ID from local state
 */
export const useLocalBranch = (branchId: string | null) => {
  const { getBranchById, isLoading, error } = useBranchContext();

  const branch = branchId ? getBranchById(branchId) : undefined;

  return {
    data: branch ? { data: branch } : null,
    isLoading,
    error,
  };
};

/**
 * Hook for creating a new branch in local state
 */
export const useLocalCreateBranch = () => {
  const { addBranch, isLoading } = useBranchContext();

  return {
    mutateAsync: addBranch,
    isPending: isLoading,
  };
};

/**
 * Hook for updating an existing branch in local state
 */
export const useLocalUpdateBranch = () => {
  const { updateBranch, isLoading } = useBranchContext();

  return {
    mutateAsync: ({ id, data }: { id: string; data: BranchFormData }) =>
      updateBranch(id, data),
    isPending: isLoading,
  };
};

/**
 * Hook for deleting a branch from local state
 */
export const useLocalDeleteBranch = () => {
  const { deleteBranch, isLoading } = useBranchContext();

  return {
    mutateAsync: deleteBranch,
    isPending: isLoading,
  };
};

/**
 * Combined hook for all branch operations
 */
export const useLocalBranchOperations = () => {
  const context = useBranchContext();

  return {
    // Data
    branches: context.branches,
    isLoading: context.isLoading,
    error: context.error,

    // Operations
    addBranch: context.addBranch,
    updateBranch: context.updateBranch,
    deleteBranch: context.deleteBranch,
    getBranchById: context.getBranchById,

    // Utilities
    clearError: context.clearError,
  };
};
